import type { User } from "./auth-types";

export interface AuthResponse {
  authenticated: boolean;
  user: User | null;
  token?: string;
  error?: string;
}

export interface AuthMessageRequest {
  type: "auth-request";
  requestId: string;
}

export interface AuthMessageResponse {
  type: "auth-response";
  requestId: string;
  data: AuthResponse;
}

export type AuthMessage = AuthMessageRequest | AuthMessageResponse;

export class IframeAuthClient {
  private parentOrigin: string;
  private requestCounter = 0;
  private pendingRequests = new Map<
    string,
    {
      resolve: (value: AuthResponse) => void;
      reject: (error: Error) => void;
      timeout: ReturnType<typeof setTimeout>;
    }
  >();

  constructor(parentOrigin: string = "*") {
    this.parentOrigin = parentOrigin;
    this.setupMessageListener();
  }

  private setupMessageListener() {
    if (typeof window === "undefined") return;

    window.addEventListener("message", (event) => {
      // Basic origin validation - in production, be more strict
      if (this.parentOrigin !== "*" && event.origin !== this.parentOrigin) {
        console.warn("Received auth message from untrusted origin:", event.origin);
        return;
      }

      const message = event.data as AuthMessage;
      if (message.type === "auth-response") {
        const pending = this.pendingRequests.get(message.requestId);
        if (pending) {
          clearTimeout(pending.timeout);
          this.pendingRequests.delete(message.requestId);
          pending.resolve(message.data);
        }
      }
    });
  }

  async getSession(): Promise<AuthResponse> {
    // Check if we're in an iframe

    // We're in iframe - request auth from parent
    return this.requestParentAuth();
  }

  async getToken(): Promise<string | null> {
    const auth = await this.getSession();
    if (!auth.authenticated || !auth.token) {
      return null;
    }
    return auth.token;
  }

  private async requestParentAuth(): Promise<AuthResponse> {
    return new Promise((resolve, reject) => {
      const requestId = `auth-${++this.requestCounter}-${Date.now()}`;

      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error("Auth request timeout"));
      }, 5000); // 5 second timeout

      this.pendingRequests.set(requestId, { resolve, reject, timeout });

      const message: AuthMessageRequest = {
        type: "auth-request",
        requestId,
      };

      window.parent.postMessage(message, this.parentOrigin);
    });
  }

  destroy() {
    // Clean up any pending requests
    for (const [, pending] of this.pendingRequests) {
      clearTimeout(pending.timeout);
      pending.reject(new Error("Auth client destroyed"));
    }
    this.pendingRequests.clear();
  }
}
